import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Bell, ClipboardCheck, CheckCircle2, AlertTriangle, FileEdit, Settings2, ChevronLeft } from 'lucide-react';
import { useTheme } from '../ui/ThemeContext';
import { useLanguage } from '../i18n/LanguageContext';
import LiquidGlassIconButton from '../ui/LiquidGlassIconButton';
import { getUserPreference, setUserPreference } from '../api/apiService';

/**
 * NotificationBell — globale Benachrichtigungs-Glocke in der Kopfleiste (Taras 29.07.).
 *
 * Absichtlich ZUSTANDS-basiert statt ereignis-basiert: die Zähler werden live aus den
 * ohnehin geladenen Daten (purchaseRequests/drafts) abgeleitet — kein eigener Backend-
 * Event-Stream, keine Gelesen-Tabelle. Einzige Persistenz: pro User die Schalter
 * ('notification-prefs') und der Zeitpunkt des letzten Öffnens ('notification-seen-at'),
 * beides über die bestehende user_preferences-API.
 *
 * Kategorien (jede per User-Schalter abschaltbar):
 *   approvals  wartende Freigaben (nur Rollen, die entscheiden dürfen) — To-do-Zähler,
 *              bleibt stehen, bis entschieden ist (bewusst: das ist eine Inbox, kein Event)
 *   decided    eigene BANFs, die seit dem letzten Öffnen genehmigt/abgelehnt wurden
 *   sapErrors  fehlgeschlagene SAP-Übertragungen — To-do-Zähler wie approvals
 *   drafts     offene Entwürfe (Default AUS — eher Erinnerung als Benachrichtigung)
 *
 * Die Glocke ist modulunabhängig immer sichtbar (Kopfleisten-Kommentar in Header.js:
 * die Beschaffungs-Icons sind kontextgebunden, die Glocke übernimmt global).
 */

const DEFAULT_PREFS = { approvals: true, decided: true, sapErrors: true, drafts: false };
const PREFS_KEY = 'notification-prefs';
const SEEN_KEY = 'notification-seen-at';

const NotificationBell = ({ user, purchaseRequests, drafts, onOpenRequests }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  // seenAt steuert, was als "neu" gilt; wird beim Schließen auf den Öffnungszeitpunkt
  // gesetzt, damit die Einträge WÄHREND des Lesens nicht aus der Liste springen.
  const [seenAt, setSeenAt] = useState(null);
  const openedAtRef = useRef(null);
  const rootRef = useRef(null);
  // Guard gegen das Lade-Race: hat der Nutzer schon getoggelt, darf der (spätere)
  // Prefs-GET die frische Auswahl nicht mehr überschreiben (Muster wie densityTouched
  // in BanfListView).
  const prefsTouched = useRef(false);

  const canDecide = user?.role === 'admin' || user?.role === 'supervisor';

  // Gespeicherte Schalter + letzter Lese-Zeitpunkt laden (best-effort, Defaults bei Fehler)
  useEffect(() => {
    if (!user?.token) return;
    let alive = true;
    (async () => {
      try {
        const p = await getUserPreference(user, PREFS_KEY);
        if (alive && !prefsTouched.current && p?.value && typeof p.value === 'object') {
          setPrefs({ ...DEFAULT_PREFS, ...p.value });
        }
      } catch { /* Defaults behalten */ }
      try {
        const s = await getUserPreference(user, SEEN_KEY);
        if (alive && typeof s?.value === 'string') setSeenAt(s.value);
      } catch { /* null = alles gilt als neu */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  // Klick außerhalb oder Escape schließt
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) closePanel();
    };
    const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groups = useMemo(() => {
    const rows = Array.isArray(purchaseRequests) ? purchaseRequests : [];
    const active = rows.filter((r) => !r.deleted_at);

    // Wartende Freigaben: nur Entscheider-Rollen. Der Supervisor-Filter ist defensiv —
    // das Backend scoped die Liste ohnehin auf die eigene Kostenstelle.
    const pending = active.filter((r) => r.status === 'pending');
    const approvals = !canDecide ? [] : user?.role === 'supervisor'
      ? pending.filter((r) => user.costCenter === r.requester_cost_center)
      : pending;

    // Eigene entschiedene BANFs seit dem letzten Öffnen (Backend liefert für die Rolle
    // 'user'/'purchaser' ohnehin nur eigene Zeilen; für Entscheider auf user_id einschränken)
    const mine = canDecide ? active.filter((r) => r.user_id === user?.id) : active;
    const decided = mine.filter((r) =>
      (r.status === 'approved' || r.status === 'rejected') &&
      r.processed_at && (!seenAt || new Date(r.processed_at) > new Date(seenAt))
    );

    // SAP-Fehler nur für Entscheider: der Retry-Endpunkt ist admin/supervisor-only —
    // eine rote Meldung ohne Handlungsoption hilft dem Anforderer nicht.
    const sapErrors = !canDecide ? [] : active.filter((r) => r.sap_transfer_status === 'failed');
    const draftsList = Array.isArray(drafts) ? drafts : [];

    return { approvals, decided, sapErrors, drafts: draftsList };
  }, [purchaseRequests, drafts, canDecide, user?.role, user?.costCenter, user?.id, seenAt]);

  const badgeCount =
    (prefs.approvals ? groups.approvals.length : 0) +
    (prefs.decided ? groups.decided.length : 0) +
    (prefs.sapErrors ? groups.sapErrors.length : 0) +
    (prefs.drafts ? groups.drafts.length : 0);

  const openPanel = () => {
    openedAtRef.current = new Date().toISOString();
    setOpen(true);
    setShowSettings(false);
    // Lese-Zeitpunkt sofort persistieren; der State folgt erst beim Schließen
    if (user?.token) setUserPreference(user, SEEN_KEY, openedAtRef.current).catch(() => {});
  };

  const closePanel = () => {
    setOpen(false);
    setShowSettings(false);
    if (openedAtRef.current) setSeenAt(openedAtRef.current);
  };

  const togglePref = (key) => {
    // next außerhalb des Updaters berechnen — Seiteneffekte im State-Updater laufen
    // unter StrictMode doppelt.
    const next = { ...prefs, [key]: !prefs[key] };
    prefsTouched.current = true;
    setPrefs(next);
    if (user?.token) setUserPreference(user, PREFS_KEY, next).catch(() => {});
  };

  const goTo = (tab) => {
    closePanel();
    if (typeof onOpenRequests === 'function') onOpenRequests(tab);
  };

  // ---- Render ---------------------------------------------------------------------------------

  const items = [
    prefs.approvals && groups.approvals.length > 0 && {
      key: 'approvals', icon: ClipboardCheck, tone: 'amber',
      count: groups.approvals.length, label: t('notif.approvals'), tab: 'pending',
    },
    prefs.sapErrors && groups.sapErrors.length > 0 && {
      key: 'sapErrors', icon: AlertTriangle, tone: 'red',
      count: groups.sapErrors.length, label: t('notif.sapErrors'), tab: 'sap_failed',
    },
    prefs.decided && groups.decided.length > 0 && {
      key: 'decided', icon: CheckCircle2, tone: 'green',
      count: groups.decided.length, label: t('notif.decided'), tab: 'all',
    },
    prefs.drafts && groups.drafts.length > 0 && {
      key: 'drafts', icon: FileEdit, tone: 'blue',
      count: groups.drafts.length, label: t('notif.drafts'), tab: 'drafts',
    },
  ].filter(Boolean);

  const toneCls = (tone) => ({
    amber: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700',
    red: isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700',
    green: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
    blue: isDarkMode ? 'bg-brand/20 text-blue-300' : 'bg-brand/10 text-brand',
  }[tone]);

  // Entscheider-Kategorien nur für Entscheider anbieten — tote Schalter verwirren nur.
  const switches = [
    canDecide && { key: 'approvals', label: t('notif.swApprovals') },
    { key: 'decided', label: t('notif.swDecided') },
    canDecide && { key: 'sapErrors', label: t('notif.swSapErrors') },
    { key: 'drafts', label: t('notif.swDrafts') },
  ].filter(Boolean);

  return (
    <div className="relative" ref={rootRef}>
      <LiquidGlassIconButton
        onClick={() => (open ? closePanel() : openPanel())}
        variant="primary"
        size="medium"
        icon={Bell}
        badge={badgeCount > 0 ? (badgeCount > 9 ? '9+' : badgeCount) : null}
        badgeColor="red"
        title={t('notif.title')}
        isActive={open}
        aria-label={t('notif.title')}
        aria-haspopup="dialog"
        aria-expanded={open}
      />

      {open && (
        <div
          role="dialog"
          aria-label={t('notif.title')}
          className={`absolute right-0 mt-2 w-80 rounded-2xl overflow-hidden z-50 shadow-xl ${isDarkMode
            ? 'bg-gray-900/95 border border-white/15 backdrop-blur-xl'
            : 'bg-white border border-slate-200'
          }`}>
          {/* Kopf */}
          <div className={`px-4 py-3 flex items-center justify-between border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
            <div className="flex items-center gap-2">
              {showSettings && (
                <button onClick={() => setShowSettings(false)} className={`p-1 rounded-lg ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                  <ChevronLeft className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
                </button>
              )}
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {showSettings ? t('notif.settingsTitle') : t('notif.title')}
              </span>
            </div>
            {!showSettings && (
              <button
                onClick={() => setShowSettings(true)}
                className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-white/10 text-white/60' : 'hover:bg-slate-100 text-slate-500'}`}
                title={t('notif.settingsTitle')}
              >
                <Settings2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Einstellungen: Schalter je Kategorie */}
          {showSettings ? (
            <div className="p-2">
              {switches.map((s) => (
                <label key={s.key} className={`flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                  <span className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{s.label}</span>
                  <button
                    role="switch"
                    aria-checked={prefs[s.key]}
                    onClick={(e) => { e.preventDefault(); togglePref(s.key); }}
                    className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${prefs[s.key]
                      ? 'bg-brand'
                      : (isDarkMode ? 'bg-white/20' : 'bg-slate-300')
                    }`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${prefs[s.key] ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                </label>
              ))}
              <p className={`px-3 pt-1 pb-2 text-[11px] ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                {t('notif.settingsHint')}
              </p>
            </div>
          ) : (
            /* Liste */
            <div className="p-2 max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className={`px-3 py-6 text-center text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                  {t('notif.empty')}
                </p>
              ) : (
                items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.key}
                      onClick={() => goTo(it.tab)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${toneCls(it.tone)}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className={`flex-1 text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>{it.label}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${toneCls(it.tone)}`}>{it.count}</span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
